/**
 * onboardingPhaseBuilder — the module-contributed onboarding phase-list builder
 * (Program-Modules epic, Wave 2).
 *
 * Extracted into its own module (no Apollo / import.meta dependencies) so it stays
 * unit-testable in isolation. useAIPMOnboarding imports `BASE_PHASES` + `buildBasePhases`
 * from here; its `phaseOrder` computed calls the builder.
 *
 * Under PROGRAM_MODULES the phase list is BUILT by concatenating any module-contributed
 * onboarding phases into BASE_PHASES at the position coaching occupies today. Coaching's
 * own hardcoded phase stays UNTOUCHED this wave (it becomes a descriptor at its retrofit);
 * the builder just supports FUTURE module phases. This wave NO descriptor declares an
 * `onboardingPhase`, so the contribution is empty and the builder returns BASE_PHASES
 * byte-for-byte — identical to the flag-off hardcoded array.
 */
import { isFeatureEnabled } from '@/constants/featureFlags';
import { getModuleDescriptors } from '@/composables/useProgramModules';

/**
 * Onboarding phase identifiers. Kept here (not re-exported from useAIPMOnboarding to
 * avoid a runtime import cycle) — useAIPMOnboarding re-exports its own `OnboardingPhase`
 * type which is structurally this same union.
 */
export type OnboardingPhaseId =
  | 'welcome'
  | 'mentor'
  | 'contact'
  | 'channel'
  | 'places'
  | 'coaching'
  | 'team'
  | 'goal'
  | 'confirmation';

/**
 * The canonical hardcoded phase list (post-welcome). Moved here from useAIPMOnboarding
 * verbatim — flag-off callers get this array byte-for-byte.
 */
export const BASE_PHASES: OnboardingPhaseId[] = [
  'mentor',
  'contact',
  'channel',
  'coaching',
  // Team Onboarding (soft/optional): sits after coaching, before goal. Like coaching, it
  // uses the HARDCODED+FILTER pattern (NOT the places descriptor-splice) — always present in
  // BASE_PHASES but filtered out of phaseOrder unless shouldShowTeam. Behind TEAM_ONBOARDING:
  // flag/gate off → filtered → byte-identical to today.
  'team',
  'goal',
  'confirmation'
];

/**
 * Map a descriptor key → its contributed onboarding phase-id. Only descriptors whose
 * KEY has a known phase-id + a mounted render branch in AIPMOnboardingDialog can
 * contribute. `places` is the FIRST real contributor (PW5); `coach` keeps its HARDCODED
 * 'coaching' phase (already in BASE_PHASES) so it is intentionally NOT re-contributed
 * here (that would duplicate it).
 */
const DESCRIPTOR_PHASE_ID: Partial<Record<string, OnboardingPhaseId>> = {
  places: 'places'
};

/**
 * Collect onboarding phases contributed by module descriptors that opt in for the
 * given context. The `places` full module (PW5) contributes its 'places' pledge phase
 * when its descriptor's `onboardingPhase.shouldShow(ctx)` passes — which is only true
 * behind PROGRAM_MODULES + PLACES_V1 (the descriptor only gains an `onboardingPhase`
 * when usePlaces().registerDescriptor() runs) AND when the context reports the program
 * has Places enabled. Flag-off / non-places programs → empty → output equals BASE_PHASES.
 */
export function collectModuleOnboardingPhases(ctx: unknown): OnboardingPhaseId[] {
  const phases: OnboardingPhaseId[] = [];
  const descriptors = getModuleDescriptors();
  (Object.keys(descriptors) as Array<keyof typeof descriptors>).forEach(k => {
    const d = descriptors[k];
    const phaseId = DESCRIPTOR_PHASE_ID[d.key];
    if (phaseId && d.onboardingPhase && d.onboardingPhase.shouldShow(ctx)) {
      if (!phases.includes(phaseId)) phases.push(phaseId);
    }
  });
  return phases;
}

/**
 * Build the base phase list. Flag OFF (or empty contribution) → BASE_PHASES
 * byte-for-byte. Flag ON with contributions → module phases inserted at coaching's
 * index (the position it occupies today).
 */
export function buildBasePhases(ctx: unknown): OnboardingPhaseId[] {
  if (!isFeatureEnabled('PROGRAM_MODULES')) return [...BASE_PHASES];
  const contributed = collectModuleOnboardingPhases(ctx);
  if (contributed.length === 0) return [...BASE_PHASES];
  const idx = BASE_PHASES.indexOf('coaching');
  const at = idx >= 0 ? idx : BASE_PHASES.length;
  return [...BASE_PHASES.slice(0, at), ...contributed, ...BASE_PHASES.slice(at)];
}
